import type { HTMLAttributes, ReactNode } from 'react';

export type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

/**
 * Non-heading elements the heading *scales* may also render as. Display, Headline
 * and Title are as often used for a value as for a heading — a score, a price, a
 * probability — and those must not land in the document outline. Without this the
 * only option was the default tag, which is why team pages were shipping scores and
 * percentages as `<h2>`.
 */
export type HeadingTextElement = HeadingLevel | 'div' | 'p' | 'span';

type VariantsFn = (...args: any[]) => string;

export interface HeadingTextProps extends Omit<HTMLAttributes<HTMLHeadingElement>, 'color'> {
    children: ReactNode;
    /**
     * Element to render. Defaults to a heading (h1 for Display, h2 for Headline and
     * Title), so anything that is *not* a section heading has to say so — pass
     * `span`/`div` for a value styled at a heading scale.
     */
    as?: HeadingTextElement;
    /** Enable text-wrap: balance to prevent widows */
    balance?: boolean;
    /** Use medium font weight */
    medium?: boolean;
    /** Use semibold font weight */
    semibold?: boolean;
    /** Show dotted underline */
    underline?: boolean;
    className?: string;
}

export interface InlineTextProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'color'> {
    children: ReactNode;
    /** Use medium font weight */
    medium?: boolean;
    /** Use semibold font weight */
    semibold?: boolean;
    /** Enable tabular numerals for aligned number columns */
    tabularNums?: boolean;
    /** Show dotted underline */
    underline?: boolean;
    className?: string;
}

/**
 * Factory for heading-level text components (Display, Headline, Title).
 * Renders a semantic heading element with tailwind-variants styling — or whatever
 * `as` names, for text that carries the heading scale without being a heading.
 */
export function createHeadingTextComponent(
    variants: VariantsFn,
    name: string,
    size: string,
    defaultTag: HeadingLevel = 'h2',
) {
    function Component({
        children,
        as: Tag = defaultTag,
        balance,
        medium,
        semibold,
        underline,
        className,
        ...props
    }: HeadingTextProps) {
        return (
            <Tag className={variants({ size, balance, medium, semibold, underline, className })} {...props}>
                {children}
            </Tag>
        );
    }
    Component.displayName = name;
    return Component;
}

/**
 * Factory for inline text components (Body, Emphasis, Overline).
 * Renders a span element with tailwind-variants styling.
 */
export function createInlineTextComponent(
    variants: VariantsFn,
    name: string,
    size: string,
    fixedVariants?: Record<string, unknown>,
) {
    function Component({ children, medium, semibold, tabularNums, underline, className, ...props }: InlineTextProps) {
        return (
            <span
                className={variants({ size, medium, semibold, tabularNums, underline, ...fixedVariants, className })}
                {...props}
            >
                {children}
            </span>
        );
    }
    Component.displayName = name;
    return Component;
}
